import { useCallback, useState, useRef, useEffect } from "react";
import Link from "next/link";

import { ApiError } from "api";

import Alert from "ui-components/Alert";
import Modal from "ui-components/Modal";
import Button from "ui-components/Button";
import Divider from "ui-components/Divider";
import InputField from "ui-components/Input-Field";

import { useGetSpec } from "./store";
import { validateSpec } from "./validator";
import { useGetCreateStackedData } from "./api";

const SaveButton = ({ fileIds }: { fileIds: string[] }) => {
  const spec = useGetSpec(fileIds);
  const specErrors = validateSpec(spec);

  const createNewFile = useGetCreateStackedData(fileIds);

  const [status, setStatus] = useState<
    "not-started" | "in-progress" | "complete"
  >("not-started");

  const [result, setResult] = useState<string>();
  const [error, setError] = useState();

  const ref = useRef<HTMLInputElement>(null);
  const onSubmit = useCallback(async () => {
    const { current } = ref;
    const name = current?.value;
    try {
      setStatus("in-progress");
      const resp = await createNewFile(name);
      setResult(resp);
    } catch (e) {
      setError(e);
    }
    setStatus("complete");
  }, [ref, createNewFile, setStatus, setResult, setError]);

  const reset = useCallback(() => {
    setStatus("not-started");
    setError(undefined);
    setResult(undefined);
  }, [setStatus, setError, setResult]);

  useEffect(() => {
    reset();
  }, [createNewFile, reset]);

  useEffect(() => {
    if (status === "complete" && result) {
    }
  }, [status, result]);

  const [createModalIsOpen, setCreateModalStatus] = useState(false);
  const openModal = () => {
    reset();
    setCreateModalStatus(true);
  };
  const closeModal = () => setCreateModalStatus(false);

  return (
    <>
      {error && <ApiError error={error} />}
      {!error && status === "complete" && result && (
        <Alert variant="success">
          <p>
            You successfully created a new file!{" "}
            <Link href={`/data-files/${result}`}>
              <span className="underline cursor-pointer">Click here</span>
            </Link>{" "}
            to see the file.
          </p>
        </Alert>
      )}
      {Object.keys(specErrors).length >= 1 && (
        <Alert variant="warning">
          <p>Fix the below before you can create a new file:</p>
          <ul className="list-inside list-disc">
            {Object.entries(specErrors).map(([key, value]) => (
              <li key={key} className="list-item">
                <span className="capitalize">{key}</span>: {value.toString()}
              </li>
            ))}
          </ul>
        </Alert>
      )}
      <Button
        className="w-full"
        disabled={Object.keys(fileIds).length <= 1}
        loading={status === "in-progress"}
        onClick={openModal}
      >
        Create New File
      </Button>
      <Modal
        id="create-new-file"
        title="Create New File"
        show={createModalIsOpen}
        handleClose={closeModal}
      >
        <InputField
          inputRef={ref}
          type="text"
          name="file-name"
          label={<span className="font-bold">Set New File Name</span>}
          required
          placeholder="New File name"
        />
        <p className="pt-1 mt-1">
          A new data file shall be created with these specs and the set file
          name.
        </p>
        {error && <ApiError error={error} />}
        {!error && status === "complete" && result && (
          <Alert variant="success">
            <p>
              You successfully created a new file!{" "}
              <Link href={`/data-files/${result}`}>
                <span className="underline cursor-pointer">Click here</span>
              </Link>{" "}
              to see the file.
            </p>
          </Alert>
        )}
        <Divider />
        <p className="text-right">
          <Button color="gray" onClick={closeModal}>
            Close
          </Button>
          <Button
            color="primary"
            loading={status === "in-progress"}
            onClick={onSubmit}
          >
            Create
          </Button>
        </p>
      </Modal>
    </>
  );
};

export default SaveButton;
